import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Coins, Sparkles, Check, Crown, Gift, ShoppingBag, ArrowRight, Users, CreditCard, Bitcoin } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bitcoin icon component
const BitcoinIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.638 14.904c-1.602 6.43-8.113 10.34-14.542 8.736C2.67 22.05-1.244 15.525.362 9.105 1.962 2.67 8.475-1.243 14.9.358c6.43 1.605 10.342 8.115 8.738 14.546zm-6.35-4.613c.24-1.59-.974-2.45-2.64-3.03l.54-2.153-1.315-.33-.52 2.107c-.345-.087-.7-.17-1.053-.25l.53-2.12-1.316-.33-.54 2.153c-.286-.064-.566-.13-.84-.18l.001-.01-1.813-.453-.35 1.407s.974.225.954.238c.532.133.628.486.612.766l-.613 2.45c.037.01.084.025.135.045l-.137-.034-.86 3.44c-.065.16-.23.4-.6.31.013.02-.954-.24-.954-.24l-.65 1.51 1.71.43c.318.08.63.163.936.24l-.54 2.18 1.313.33.54-2.16c.36.1.705.19 1.05.27l-.54 2.14 1.316.33.54-2.17c2.24.423 3.926.253 4.64-1.774.57-1.637-.03-2.58-1.21-3.2.86-.2 1.51-.77 1.68-1.93h.01zm-3.01 4.22c-.404 1.64-3.157.75-4.05.53l.72-2.9c.896.23 3.757.67 3.33 2.37zm.41-4.24c-.37 1.49-2.662.735-3.405.55l.654-2.64c.744.186 3.137.54 2.75 2.09z"/>
  </svg>
);

export default function Credits() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('stripe'); // 'stripe' or 'crypto'

  useEffect(() => {
    // Check for cancelled payment
    if (searchParams.get('cancelled')) {
      toast.error(t('tokens.paymentCancelled') || 'Payment cancelled');
    }

    // Fetch packages
    axios.get(`${API}/credits/packages`)
      .then(res => {
        setPackages(res.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));

    // Fetch balance if logged in
    if (user) {
      const token = localStorage.getItem('token');
      axios.get(`${API}/credits/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => setBalance(res.data?.balance || 0))
        .catch(() => {});
    }
  }, [user, searchParams, t]);

  const handlePurchase = async (packageId) => {
    if (!user) {
      toast.error(t('auth.loginRequired') || 'Please login first');
      navigate('/login');
      return;
    }

    setPurchasing(packageId);
    try {
      const token = localStorage.getItem('token');
      
      if (paymentMethod === 'crypto') {
        // Crypto payment via Coinbase
        const res = await axios.post(`${API}/credits/purchase/crypto`, {
          package_id: packageId,
          origin_url: window.location.origin
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (res.data?.checkout_url) {
          window.location.href = res.data.checkout_url;
        }
      } else {
        // Stripe payment
        const res = await axios.post(`${API}/credits/purchase`, {
          package_id: packageId,
          origin_url: window.location.origin
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (res.data?.checkout_url) {
          window.location.href = res.data.checkout_url;
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('builder.checkoutError') || 'Could not start checkout');
      setPurchasing(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-sm mb-6">
            <Coins className="w-4 h-4" />
            {t('tokens.badge')}
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            {t('tokens.title')} <span className="text-[#D4AF37]">{t('tokens.titleAccent')}</span>
          </h1>
          
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
            {t('tokens.subtitle')}
          </p>

          {/* Current Balance */}
          {user && (
            <div className="inline-flex items-center gap-4 px-6 py-4 rounded-2xl bg-gradient-to-r from-[#D4AF37]/20 to-[#8B7025]/20 border border-[#D4AF37]/30">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center">
                <Coins className="w-6 h-6 text-black" />
              </div>
              <div className="text-left">
                <p className="text-sm text-gray-400">{t('tokens.yourBalance')}</p>
                <p className="text-3xl font-bold text-[#D4AF37]">{balance} <span className="text-lg">{t('tokens.tokenUnit')}</span></p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Packages */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Payment Method Selector */}
          <div className="flex justify-center gap-4 mb-10">
            <button
              onClick={() => setPaymentMethod('stripe')}
              className={`flex items-center gap-3 px-6 py-4 rounded-xl border-2 transition-all ${
                paymentMethod === 'stripe' 
                  ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                  : 'border-white/10 bg-white/5 hover:border-white/30'
              }`}
            >
              <CreditCard className={`w-6 h-6 ${paymentMethod === 'stripe' ? 'text-[#D4AF37]' : 'text-gray-400'}`} />
              <div className="text-left">
                <p className={`font-medium ${paymentMethod === 'stripe' ? 'text-[#D4AF37]' : 'text-white'}`}>Credit Card</p>
                <p className="text-xs text-gray-400">Visa, Mastercard, etc.</p>
              </div>
              {paymentMethod === 'stripe' && <Check className="w-5 h-5 text-[#D4AF37] ml-2" />}
            </button>
            
            <button
              onClick={() => setPaymentMethod('crypto')}
              className={`flex items-center gap-3 px-6 py-4 rounded-xl border-2 transition-all ${
                paymentMethod === 'crypto' 
                  ? 'border-[#F7931A] bg-[#F7931A]/10' 
                  : 'border-white/10 bg-white/5 hover:border-white/30'
              }`}
            >
              <BitcoinIcon className={`w-6 h-6 ${paymentMethod === 'crypto' ? 'text-[#F7931A]' : 'text-gray-400'}`} />
              <div className="text-left">
                <p className={`font-medium ${paymentMethod === 'crypto' ? 'text-[#F7931A]' : 'text-white'}`}>Crypto</p>
                <p className="text-xs text-gray-400">Bitcoin, Ethereum, etc.</p>
              </div>
              {paymentMethod === 'crypto' && <Check className="w-5 h-5 text-[#F7931A] ml-2" />}
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {packages.map((pkg) => (
              <div 
                key={pkg.id}
                className={`relative rounded-2xl p-6 transition-all duration-300 ${
                  pkg.is_popular 
                    ? 'bg-gradient-to-b from-[#D4AF37]/20 to-[#0A0A0A] border-2 border-[#D4AF37] scale-105' 
                    : 'bg-white/5 border border-white/10 hover:border-[#D4AF37]/50'
                }`}
              >
                {pkg.is_popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#D4AF37] text-black text-sm font-bold rounded-full">
                    {t('tokens.popular')}
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4 ${
                    pkg.is_popular ? 'bg-[#D4AF37]' : 'bg-white/10'
                  }`}>
                    <Coins className={`w-8 h-8 ${pkg.is_popular ? 'text-black' : 'text-[#D4AF37]'}`} />
                  </div>
                  
                  <h3 className="text-2xl font-bold mb-2">{pkg.credits} Tokens</h3>
                  
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-[#D4AF37]">€{pkg.price}</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-[#D4AF37]" />
                    <span>{pkg.credits} {t('tokens.tokenUnit')}</span>
                  </li>
                  {pkg.bonus > 0 && (
                    <li className="flex items-center gap-3 text-green-400">
                      <Gift className="w-5 h-5" />
                      <span>+{pkg.bonus} {t('tokens.bonus')} {t('tokens.tokenUnit')}!</span>
                    </li>
                  )}
                  <li className="flex items-center gap-3 text-gray-300">
                    <ShoppingBag className="w-5 h-5 text-[#D4AF37]" />
                    <span>{t('tokens.features.spendInShop')}</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-300">
                    <Crown className="w-5 h-5 text-[#D4AF37]" />
                    <span>{t('tokens.features.neverExpire')}</span>
                  </li>
                </ul>

                <Button
                  onClick={() => handlePurchase(pkg.id)}
                  disabled={purchasing === pkg.id}
                  className="w-full py-6 text-lg font-semibold bg-[#D4AF37] hover:bg-[#B8963A] text-black"
                  data-testid={`buy-${pkg.id}`}
                >
                  {purchasing === pkg.id ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      {t('common.loading')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      {t('tokens.cta')}
                      <ArrowRight className="w-5 h-5" />
                    </span>
                  )}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Referral Banner */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="p-8 glass rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-r from-[#D4AF37]/10 to-transparent">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-20 h-20 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                <Users className="w-10 h-10 text-[#D4AF37]" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl font-bold mb-2">{t('howItWorks.referral.title')}</h3>
                <p className="text-gray-400">{t('howItWorks.referral.desc')}</p>
              </div>
              <Button
                onClick={() => navigate('/how-it-works')}
                variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
              >
                {t('common.learnMore') || 'Learn More'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 px-6 border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            {t('tokens.howItWorks.title')}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#D4AF37]/20 flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-[#D4AF37]">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">{t('tokens.howItWorks.step1Title')}</h3>
              <p className="text-gray-400">{t('tokens.howItWorks.step1Desc')}</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#D4AF37]/20 flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-[#D4AF37]">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">{t('tokens.howItWorks.step2Title')}</h3>
              <p className="text-gray-400">{t('tokens.howItWorks.step2Desc')}</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#D4AF37]/20 flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-[#D4AF37]">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">{t('tokens.howItWorks.step3Title')}</h3>
              <p className="text-gray-400">{t('tokens.howItWorks.step3Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6">
        <div className="max-w-2xl mx-auto text-center space-x-4">
          <Button
            onClick={() => navigate('/shop')}
            variant="outline"
            className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
          >
            <ShoppingBag className="w-5 h-5 mr-2" />
            {t('tokens.viewShop')}
          </Button>
          <Button
            onClick={() => navigate('/how-it-works')}
            className="bg-[#D4AF37] hover:bg-[#B8963A] text-black"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            {t('howItWorks.badge') || 'How It Works'}
          </Button>
        </div>
      </section>
    </div>
  );
}
